/**
 * Offline play for every game: one human against bots, with no server at all.
 *
 * {@link OfflineGame} is a headless driver (no timers, no I/O) so the browser can pace it however it likes and
 * tests can run a whole game instantly. It plays a real table's rules (entry fee, seats, payouts and bot
 * strength come from the table config the device cached while online) and records every step in the same
 * {@link ReplayEntry} format the live tables log, so the server can re-run the finished game with
 * {@link verifyOfflineGame} before it moves a single coin.
 *
 * - `match` games (Call Break, Hearts, …) are played to the end; the human pays the entry fee and is paid by
 *   finishing place.
 * - `session` games (Poker, Blackjack, …) are played hand by hand with a buy-in; the human cashes out when they
 *   leave or run out of chips, and the net result is `chips − buy-in`.
 */
import { DIFFICULTY_PROFILES, makeBotContext, type BotDifficulty } from './bots/types.js';
import { RecordingRng, ReplayError, ReplayRng, type ReplayEntry } from './core/replay.js';
import { CryptoRng, type Rng } from './core/rng.js';
import { EngineError, type AnyGameEngine, type EngineEvent, type GameType, type SeatSetup, type Standing } from './core/types.js';
import { OFFLINE_HUMAN_SEAT } from './offline.js';
import { buildEngineConfig, getGameModule } from './registry.js';
import type { AnyBotEngine } from './bots/types.js';

export { OFFLINE_HUMAN_SEAT };

/** Longest log a single offline game may produce; a session is cashed out before it gets here. */
export const OFFLINE_MAX_LOG = 4000;

/** The part of a table config an offline game needs (all of it is also known to the server). */
export interface OfflineTableSpec {
  gameType: GameType;
  rules: Record<string, unknown>;
  /** Match games: the entry fee. Session games: the buy-in. 0 = free practice table. */
  entryFee: number;
  minPlayers: number;
  maxPlayers: number;
  difficulty: BotDifficulty;
}

export const offlineBuyIn = (spec: OfflineTableSpec): number =>
  spec.entryFee > 0 ? spec.entryFee : typeof spec.rules.practiceChips === 'number' && spec.rules.practiceChips > 0 ? spec.rules.practiceChips : 1000;

/** How many players a session table keeps seated (the live tables leave one chair free). */
export const offlineSessionTarget = (spec: OfflineTableSpec): number => Math.max(spec.minPlayers, spec.maxPlayers - 1);

/** Fewest chips that still let a player take part in the next hand (mirrors the live table). */
export function offlineMinChips(gameType: GameType, config: unknown): number {
  const c = config as { boot?: number; bigBlind?: number; pointValue?: number; mode?: string; minBet?: number };
  if (gameType === 'TEEN_PATTI' || gameType === 'FLUSH' || gameType === 'BRAG') return c.boot ?? 1;
  if (gameType === 'BLACKJACK' || gameType === 'ANDAR_BAHAR' || gameType === 'BACCARAT' || gameType === 'DRAGON_TIGER' || gameType === 'CASINO_WAR' || gameType === 'THREE_CARD_POKER' || gameType === 'RED_DOG' || gameType === 'PAI_GOW') return c.minBet ?? 1;
  if (gameType === 'POKER') return c.mode === 'tournament' ? 1 : (c.bigBlind ?? 1);
  if (gameType === 'MARRIAGE') return Math.max(1, (c.pointValue ?? 1) * 10);
  return 1;
}

export interface HandOutcome {
  /** Chips won (+) or lost (−) this hand, by seat, for everyone still seated when it ended. */
  deltas: Record<number, number>;
  end: EngineEvent;
}

/** Works out per-hand chip changes of a session game from its events — shared by the driver and the verifier. */
export class HandTracker {
  private start: Record<number, number> = {};
  private done: HandOutcome[] = [];
  constructor(private readonly engine: AnyGameEngine) {}

  /** Call after every engine step with the state and the events it produced. */
  observe(state: unknown, events: readonly EngineEvent[]): void {
    if (!this.engine.getChips) return;
    for (const e of events) {
      if (e.type === 'handStart') {
        const chips = this.engine.getChips(state);
        const st = state as { seats?: ({ invested?: number; total?: number } | null)[] };
        this.start = {};
        for (const [seat, c] of Object.entries(chips)) {
          const p = st.seats?.[Number(seat)];
          this.start[Number(seat)] = c + (p?.invested ?? p?.total ?? 0);
        }
      } else if (e.type === 'handEnd') {
        const chips = this.engine.getChips(state);
        const deltas: Record<number, number> = {};
        for (const [seat, before] of Object.entries(this.start)) {
          const now = chips[Number(seat)];
          if (now !== undefined) deltas[Number(seat)] = now - before;
        }
        this.done.push({ deltas, end: e });
      }
    }
  }

  /** Hands that finished since the last call. */
  drain(): HandOutcome[] {
    const d = this.done;
    this.done = [];
    return d;
  }
}

export interface OfflineMember {
  seat: number;
  isBot: boolean;
  /** 1-based number of the bot, for naming (0 for the human). */
  botNo: number;
}

export class OfflineGame {
  readonly engine: AnyGameEngine;
  readonly bot: AnyBotEngine;
  readonly kind: 'match' | 'session';
  readonly config: unknown;
  readonly log: ReplayEntry[] = [];
  readonly buyIn: number;
  private readonly state: unknown;
  private readonly rec: RecordingRng;
  private readonly botRng: Rng;
  private readonly tracker: HandTracker;
  private readonly minChips: number;
  private botCounter = 0;
  private members: (OfflineMember | null)[];
  private humanOut = false;
  private cashout = 0;
  /** True once the human left a match early: the bots played their seat to the end. */
  abandoned = false;

  constructor(
    readonly spec: OfflineTableSpec,
    rng: Rng = new CryptoRng(),
  ) {
    const mod = getGameModule(spec.gameType);
    this.engine = mod.engine;
    this.bot = mod.bot;
    this.kind = this.engine.kind;
    this.rec = new RecordingRng(rng);
    this.botRng = new CryptoRng();
    this.tracker = new HandTracker(this.engine);
    this.buyIn = offlineBuyIn(spec);
    this.config = buildEngineConfig(spec.gameType, spec.rules, spec.maxPlayers, spec.minPlayers);
    this.minChips = offlineMinChips(spec.gameType, this.config);
    this.members = Array<OfflineMember | null>(spec.maxPlayers).fill(null);
    if (this.kind === 'match') {
      const seats: SeatSetup[] = Array.from({ length: spec.maxPlayers }, (_, seat) => ({ seat, playerId: seat === OFFLINE_HUMAN_SEAT ? 'me' : `bot${seat}` }));
      this.state = this.engine.createState(this.config, seats, this.rec);
      this.log.push({ kind: 'create', config: this.config, seats, draws: this.rec.take() });
      seats.forEach((s) => (this.members[s.seat] = { seat: s.seat, isBot: s.seat !== OFFLINE_HUMAN_SEAT, botNo: s.seat }));
    } else {
      this.state = this.engine.createState(this.config, [], this.rec);
      this.log.push({ kind: 'create', config: this.config, seats: [], draws: this.rec.take() });
      this.seatIn(OFFLINE_HUMAN_SEAT, 'me', false);
      this.fillBots();
    }
  }

  // ---------------------------------------------------------------- queries
  get finished(): boolean {
    return this.kind === 'session' ? this.humanOut : this.engine.isFinished(this.state);
  }
  /** Session games: the human has cashed out (left, or ran out of chips). */
  get cashedOut(): boolean {
    return this.humanOut;
  }
  get cashoutChips(): number {
    return this.cashout;
  }
  /** Seat whose input is awaited, or null between steps. */
  get actor(): number | null {
    return this.engine.getActors(this.state)[0] ?? null;
  }
  get systemStep(): { kind: string; delayMs: number } | null {
    return this.engine.getSystemStep(this.state);
  }
  /** True when the log is about to exceed what the server accepts: cash out and start a new session. */
  get logFull(): boolean {
    return this.log.length >= OFFLINE_MAX_LOG - 150;
  }
  get roundsCompleted(): number {
    return this.engine.getRoundsCompleted(this.state);
  }
  standings(): Standing[] {
    return this.engine.getStandings(this.state);
  }
  chips(): Record<number, number> {
    return this.engine.getChips?.(this.state) ?? {};
  }
  seatMembers(): (OfflineMember | null)[] {
    return this.members;
  }
  view(seat = OFFLINE_HUMAN_SEAT): unknown {
    return this.engine.getView(this.state, seat);
  }
  legal(seat = OFFLINE_HUMAN_SEAT): unknown {
    return this.engine.getLegalActions(this.state, seat);
  }
  timeoutAction(seat = OFFLINE_HUMAN_SEAT): unknown {
    return this.engine.getTimeoutAction(this.state, seat);
  }
  /** Session hands that finished since the last call. */
  takeHands(): HandOutcome[] {
    return this.tracker.drain();
  }
  /** Rough "thinking" delay for a bot, so offline pacing feels like a live table. */
  thinkMs(): number {
    const [lo, hi] = DIFFICULTY_PROFILES[this.spec.difficulty].thinkMs;
    return lo + this.botRng.int(Math.max(1, hi - lo));
  }

  // ---------------------------------------------------------------- stepping
  private track(events: EngineEvent[]): EngineEvent[] {
    this.tracker.observe(this.state, events);
    return events;
  }

  /** Applies a (validated) action for `seat`. Throws {@link EngineError} if it is illegal. */
  act(seat: number, raw: unknown): EngineEvent[] {
    const action = this.engine.parseAction(raw);
    const events = this.engine.applyAction(this.state, seat, action, this.rec);
    this.log.push({ kind: 'action', seat, action, draws: this.rec.take() });
    return this.track(events);
  }

  /** Lets the bot in `seat` play its move. */
  botMove(seat: number): EngineEvent[] {
    const difficulty = seat === OFFLINE_HUMAN_SEAT ? 'NORMAL' : this.spec.difficulty;
    try {
      const action = this.bot.decideAction(this.engine.getView(this.state, seat), this.engine.getLegalActions(this.state, seat), makeBotContext(seat, difficulty, this.botRng));
      return this.act(seat, action);
    } catch {
      // A bot must never stall a game: fall back to the engine's safe move.
      return this.act(seat, this.engine.getTimeoutAction(this.state, seat));
    }
  }

  /** Runs the pending automatic step (collect a trick, start the next round or hand). */
  runSystemStep(): EngineEvent[] {
    const out: EngineEvent[] = [];
    const step = this.engine.getSystemStep(this.state);
    if (this.kind === 'session' && step && (step.kind === 'nextHand' || step.kind === 'startHand')) {
      out.push(...this.betweenHands());
      if (this.humanOut) return out;
    }
    const events = this.engine.runSystemStep(this.state, this.rec);
    this.log.push({ kind: 'system', draws: this.rec.take() });
    out.push(...this.track(events));
    return out;
  }

  /** Session games: the human leaves the table and takes their chips (a running hand is folded). */
  leave(): EngineEvent[] {
    if (this.kind !== 'session' || this.humanOut) return [];
    return this.cashOutHuman();
  }

  /** Match games: the human walks away, so the bots finish the game for them (they forfeit the prize, as online). */
  forfeit(): void {
    if (this.kind !== 'match' || this.finished) return;
    this.abandoned = true;
    for (let guard = 0; guard < OFFLINE_MAX_LOG && !this.finished; guard++) {
      const actor = this.actor;
      if (actor !== null) this.botMove(actor);
      else if (this.systemStep) this.runSystemStep();
      else return;
    }
  }

  /** Plays everything that does not need the human, up to the human's next decision (or the end). Tests/simulation only. */
  advance(): void {
    for (let guard = 0; guard < OFFLINE_MAX_LOG * 4 && !this.finished; guard++) {
      const actor = this.actor;
      if (actor === OFFLINE_HUMAN_SEAT) return;
      if (actor !== null) this.botMove(actor);
      else if (this.systemStep) this.runSystemStep();
      else return;
    }
  }

  // ---------------------------------------------------------------- session housekeeping
  private seatIn(seat: number, playerId: string, isBot: boolean): void {
    const setup: SeatSetup = { seat, playerId, chips: this.buyIn };
    const events = this.engine.addSeat!(this.state, setup);
    this.log.push({ kind: 'addSeat', setup });
    this.members[seat] = { seat, isBot, botNo: isBot ? ++this.botCounter : 0 };
    this.track(events);
  }

  private fillBots(): void {
    const target = offlineSessionTarget(this.spec);
    while (this.members.filter(Boolean).length < Math.min(target, this.spec.maxPlayers)) {
      const seat = this.members.findIndex((m) => !m);
      if (seat < 0) break;
      this.seatIn(seat, `bot${this.botCounter + 1}`, true);
    }
  }

  private cashOutHuman(): EngineEvent[] {
    const { chips, events } = this.engine.removeSeat!(this.state, OFFLINE_HUMAN_SEAT);
    this.log.push({ kind: 'removeSeat', seat: OFFLINE_HUMAN_SEAT });
    this.members[OFFLINE_HUMAN_SEAT] = null;
    this.humanOut = true;
    this.cashout = chips;
    return this.track(events);
  }

  private betweenHands(): EngineEvent[] {
    const out: EngineEvent[] = [];
    const chips = this.chips();
    for (const m of [...this.members]) {
      if (!m) continue;
      const c = chips[m.seat] ?? 0;
      if (m.isBot && c < this.minChips) {
        const r = this.engine.removeSeat!(this.state, m.seat);
        this.log.push({ kind: 'removeSeat', seat: m.seat });
        this.members[m.seat] = null;
        out.push(...this.track(r.events));
      } else if (!m.isBot && (c < this.minChips || this.logFull)) {
        out.push(...this.cashOutHuman());
        return out;
      }
    }
    this.fillBots();
    return out;
  }
}

// ====================================================================== verification

export interface VerifiedHand {
  delta: number;
  won: boolean;
}

export type VerifiedOfflineGame =
  | { kind: 'match'; place: number; standings: Standing[]; rounds: number }
  | { kind: 'session'; chips: number; buyIn: number; hands: VerifiedHand[]; rounds: number };

const stable = (v: unknown): string =>
  JSON.stringify(v, (_k, x: unknown) => (x && typeof x === 'object' && !Array.isArray(x) ? Object.fromEntries(Object.entries(x as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : 1))) : x));

/**
 * Re-runs a recorded offline game on the real engine against the table the server knows. Throws
 * {@link ReplayError} unless the log is a complete, legal game of exactly that table; otherwise returns how the
 * human fared. Nothing in the log is trusted: the config, seats and buy-ins are compared with the table.
 */
export function verifyOfflineGame(spec: OfflineTableSpec, entries: readonly ReplayEntry[], maxEntries = OFFLINE_MAX_LOG): VerifiedOfflineGame {
  try {
    return verify(spec, entries, maxEntries);
  } catch (err) {
    if (err instanceof ReplayError) throw err;
    // Untrusted input: anything malformed enough to crash the engine is simply an invalid log.
    throw new ReplayError(err instanceof Error ? `Malformed log: ${err.message}` : 'Malformed log');
  }
}

function verify(spec: OfflineTableSpec, entries: readonly ReplayEntry[], maxEntries: number): VerifiedOfflineGame {
  const engine = getGameModule(spec.gameType).engine;
  const first = entries[0];
  if (!first || first.kind !== 'create') throw new ReplayError('Log must start with a create entry');
  if (entries.length > maxEntries) throw new ReplayError('Log too long');
  const expected = buildEngineConfig(spec.gameType, spec.rules, spec.maxPlayers, spec.minPlayers);
  if (stable(first.config) !== stable(expected)) throw new ReplayError('Game settings do not match the table');
  const session = engine.kind === 'session';
  const buyIn = offlineBuyIn(spec);
  if (session) {
    if (first.seats.length !== 0) throw new ReplayError('A session starts with empty seats');
  } else {
    if (first.seats.length !== spec.maxPlayers || first.seats.some((s, i) => s.seat !== i)) throw new ReplayError('Wrong number of seats');
    if (first.seats[OFFLINE_HUMAN_SEAT]!.playerId !== 'me' || first.seats.some((s, i) => i !== OFFLINE_HUMAN_SEAT && s.playerId === 'me')) throw new ReplayError('Exactly one human seat is expected');
  }
  const tracker = new HandTracker(engine);
  const hands: VerifiedHand[] = [];
  let humanSeated = false;
  let humanChips: number | null = null;
  let state: unknown;
  try {
    const rng = new ReplayRng(first.draws);
    state = engine.createState(engine.normalizeConfig(first.config as never), first.seats, rng);
    if (!rng.exhausted) throw new ReplayError('Unused random draws (create)');
    for (let i = 1; i < entries.length; i++) {
      const e = entries[i]!;
      if (humanChips !== null) throw new ReplayError('Nothing may follow the human cashing out');
      let events: EngineEvent[] = [];
      if (e.kind === 'action') {
        const r = new ReplayRng(e.draws);
        events = engine.applyAction(state, e.seat, engine.parseAction(e.action), r);
        if (!r.exhausted) throw new ReplayError(`Unused random draws (step ${i})`);
      } else if (e.kind === 'system') {
        const r = new ReplayRng(e.draws);
        events = engine.runSystemStep(state, r);
        if (!r.exhausted) throw new ReplayError(`Unused random draws (step ${i})`);
      } else if (e.kind === 'addSeat' && session) {
        const { seat, playerId, chips } = e.setup;
        if (chips !== buyIn) throw new ReplayError('Wrong buy-in');
        if (seat === OFFLINE_HUMAN_SEAT || playerId === 'me') {
          if (humanSeated || seat !== OFFLINE_HUMAN_SEAT || playerId !== 'me') throw new ReplayError('Exactly one human seat is expected');
          humanSeated = true;
        } else if (!humanSeated) throw new ReplayError('The human sits down first');
        events = engine.addSeat!(state, e.setup);
      } else if (e.kind === 'removeSeat' && session) {
        if (!humanSeated) throw new ReplayError('The human sits down first');
        const r = engine.removeSeat!(state, e.seat);
        events = r.events;
        if (e.seat === OFFLINE_HUMAN_SEAT) humanChips = r.chips;
      } else throw new ReplayError(`Unexpected entry ${e.kind}`);
      tracker.observe(state, events);
      for (const h of tracker.drain()) {
        const d = h.deltas[OFFLINE_HUMAN_SEAT];
        if (d !== undefined) hands.push({ delta: d, won: d > 0 });
      }
    }
  } catch (err) {
    if (err instanceof EngineError) throw new ReplayError(`Rejected by the engine: ${err.message}`);
    throw err;
  }
  const rounds = engine.getRoundsCompleted(state);
  if (session) {
    if (humanChips === null) throw new ReplayError('The human never cashed out');
    return { kind: 'session', chips: humanChips, buyIn, hands, rounds };
  }
  if (!engine.isFinished(state)) throw new ReplayError('Game is not finished');
  const standings = engine.getStandings(state);
  const mine = standings.find((s) => s.seat === OFFLINE_HUMAN_SEAT);
  if (!mine) throw new ReplayError('Human seat missing from standings');
  return { kind: 'match', place: mine.place, standings, rounds };
}
